import React, { useEffect, useMemo, useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

import { Tag, Empty, Space, Input, Select, Pagination } from "antd";
import {
    HomeOutlined,
    SearchOutlined,
    MenuOutlined,
} from "@ant-design/icons";

import { ENDPOINTS } from "../../../Services/ENDPOINT";
import { RoomServices } from "../../../Services/RoomServices";
import { notify } from "../../Customs/NotifyProvider";

const getRoomPriority = (name = "") => {
    const firstChar = String(name).trim().charAt(0);
    if (/^[^a-zA-Z0-9]/.test(firstChar)) return 1;
    if (/^[0-9]/.test(firstChar)) return 2;
    return 3;
};

const sortRooms = (rooms = []) => {
    return [...rooms].sort((a, b) => {
        const aHasSort =
            a.sort_order !== null &&
            a.sort_order !== undefined &&
            a.sort_order !== "";
        const bHasSort =
            b.sort_order !== null &&
            b.sort_order !== undefined &&
            b.sort_order !== "";

        if (aHasSort && bHasSort) return Number(a.sort_order) - Number(b.sort_order);
        if (aHasSort) return -1;
        if (bHasSort) return 1;

        const priorityDiff = getRoomPriority(a.room_name) - getRoomPriority(b.room_name);
        if (priorityDiff !== 0) return priorityDiff;

        return String(a.room_name).localeCompare(String(b.room_name), undefined, {
            numeric: true,
            sensitivity: "base",
        });
    });
};

const SortableRoomItem = ({ room, selectedRow, onRowSelect }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: room.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const isSelected = selectedRow?.id === room.id;

    const images = (() => {
        try {
            const parsed = typeof room.images === "string" ? JSON.parse(room.images) : room.images;
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    })();

    const thumb = images[0] ? `${ENDPOINTS.cleanBase}${images[0]}` : null;

    return (
        <div
            ref={setNodeRef}
            style={{
                ...style,
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                borderRadius: 10,
                cursor: "pointer",
                border: isSelected ? "2px solid #1677ff" : "1px solid #f0f0f0",
                background: isSelected ? "#e6f4ff" : "#fff",
                boxShadow: isSelected
                    ? "0 2px 8px rgba(22,119,255,.15)"
                    : "0 1px 3px rgba(0,0,0,.06)",
                transition: "all .2s",
            }}
            onClick={() => onRowSelect(room)}
        >
            <div
                {...attributes}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: "grab", padding: 6, display: "flex", alignItems: "center", color: "#999" }}
            >
                <MenuOutlined />
            </div>

            <div
                style={{
                    width: 56, height: 56, borderRadius: 8, overflow: "hidden",
                    flexShrink: 0, background: "#f5f5f5",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}
            >
                {thumb ? (
                    <img src={thumb} alt="room" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                    <HomeOutlined style={{ fontSize: 22, color: "#bfbfbf" }} />
                )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {room.room_name || `Room ${room.id}`}
                </div>
                <div style={{ fontSize: 13, color: "#000000ff", marginTop: 2 }}>
                    {room.room_type} | Floor {room.floor} | Room No.: {room.room_number}
                </div>
                <Space>
                    <div style={{ fontSize: 16, color: "#9e0d0dff", marginTop: 2 }}>
                        ₹{room.price_per_day}/day
                    </div>
                    <div style={{ fontSize: 16, color: "#07002bff", marginTop: 2 }}>
                        Max Occupancy: {room.max_occupancy}
                    </div>
                </Space>
            </div>

            {/* <Tag
                color={room.status === "available" ? "success" : "error"}
                style={{ borderRadius: 99, flexShrink: 0 }}
            >
                {room.status?.toUpperCase()}
            </Tag> */}
        </div>
    );
};

const RoomList = ({ dataSource = [], selectedRow, onRowSelect }) => {
    const [searchText, setSearchText] = useState("");
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setRooms(sortRooms(dataSource));
    }, [dataSource]);

    // ── Derive unique floor options from dataSource ───────────────────────
    const floorOptions = useMemo(() => {
        const unique = [...new Set(dataSource.map((r) => r.floor))]
            .filter((f) => f !== null && f !== undefined && f !== "")
            .sort((a, b) =>
                String(a).localeCompare(String(b), undefined, { numeric: true })
            );

        return unique.map((f) => ({ label: `Floor ${f}`, value: String(f) }));
    }, [dataSource]);
    // ─────────────────────────────────────────────────────────────────────

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const filteredData = useMemo(() => {
        return rooms.filter((item) => {
            // Floor filter
            if (selectedFloor !== null && String(item.floor) !== selectedFloor) {
                return false;
            }

            // Search filter
            const search = searchText.toLowerCase();
            return Object.values(item).some((value) => {
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(search);
            });
        });
    }, [rooms, searchText, selectedFloor]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, selectedFloor]);

    useEffect(() => {
        if (!selectedRow?.id) return;

        const selectedIndex = filteredData.findIndex(
            (item) => item.id === selectedRow.id
        );

        if (selectedIndex !== -1) {
            setCurrentPage(Math.floor(selectedIndex / pageSize) + 1);
        }
    }, [selectedRow, filteredData, pageSize]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    const handleDragEnd = async (event) => {
        try {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const oldIndex = rooms.findIndex((item) => item.id === active.id);
            const newIndex = rooms.findIndex((item) => item.id === over.id);
            const updatedRooms = arrayMove(rooms, oldIndex, newIndex);

            setRooms(updatedRooms);

            const reordered = updatedRooms.map((room, index) => ({
                id: room.id,
                sort_order: index + 1,
            }));

            // console.log("NEW ROOM ORDER:", reordered);

            const req = await RoomServices.roomReIndex(reordered);

            if (req.status) {
                notify.success(req.message || "Room order updated successfully");
            } else {
                notify.error(req.message || "Failed to update room order");
            }
        } catch (error) {
            console.error("Drag reorder error:", error);
            notify.error(error?.response?.data?.message || "Something went wrong");
        }
    };

    return (
        <>
            <Space style={{ paddingBottom: "12px" }}>
                <Input
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    style={{ minWidth: 'auto', width: '500px' }}
                    size="large"
                    placeholder="Search..."
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />

                {/* ── Floor filter ─────────────────────────────────────────── */}
                <Select
                    style={{ width: 130 }}
                    size="large"
                    placeholder="All Floors"
                    options={floorOptions}
                    value={selectedFloor}
                    onChange={(val) => setSelectedFloor(val ?? null)}
                    allowClear
                    onClear={() => setSelectedFloor(null)}
                />
                {/* ─────────────────────────────────────────────────────────── */}
            </Space>

            {filteredData.length === 0 ? (
                <div style={{ padding: 24 }}>
                    <Empty description="No rooms found!" />
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={paginatedData.map((room) => room.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {paginatedData.map((room) => (
                                <SortableRoomItem
                                    key={room.id}
                                    room={room}
                                    selectedRow={selectedRow}
                                    onRowSelect={onRowSelect}
                                />
                            ))}
                        </div>
                    </SortableContext>

                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredData.length}
                        showSizeChanger
                        pageSizeOptions={["5", "10", "20", "50"]}
                        onChange={(page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        style={{
                            marginTop: 16,
                            marginBottom: 16,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    />

                </DndContext>
            )}
        </>
    );
};

export default RoomList;